(() => {
  const script = document.currentScript;
  const root = document.documentElement;
  const stage = document.getElementById('jb-curtain-entrance');
  if (!stage || !script) return;
  if (window.JBCurtainEntrance?.element === stage) return;
  window.JBCurtainEntrance?.destroy?.();
  const dark = new Image();
  dark.crossOrigin = 'anonymous';
  dark.decoding = 'async';
  let canvas = stage.querySelector('canvas');
  let renderer = null, width = 0, height = 0, opening = 0, lighting = 0;
  let prepared = false, preparation = null, frame = 0, completion = null, activePlay = null;
  let settlePreparation = null;
  let preparationFailed = false, retryImage = null, loadAttempt = 0;
  const controller = new AbortController();
  const clamp = value => Math.max(0, Math.min(1, value));
  const ease = value => { value = clamp(value); return value * value * value * (value * (value * 6 - 15) + 10); };
  const vertexSource=`precision highp float;
    attribute vec2 position;
    uniform mediump float opening; uniform float side; uniform mediump float photoSpan;
    varying mediump vec2 uv;
    varying mediump vec3 surface;
    varying mediump vec3 normal;
    float ease(float t){t=clamp(t,0.0,1.0);return t*t*t*(t*(t*6.0-15.0)+10.0);}
    vec3 cloth(vec2 q){
      float delay=side>0.0?0.018:0.0;
      float g=ease((opening-delay-0.045*pow(clamp(q.y,0.0,1.0),1.7))/0.94);
      float tension=sin(g*3.14159265);
      float outer=1.0+0.30*g;
      float span=1.0-0.75*g;
      float folds=sin(q.x*34.0/photoSpan+0.45*sin(q.x*12.0/photoSpan)+q.y*0.7+g*0.55);
      float weight=pow(clamp(q.x,0.0,1.0),1.8)*sin(q.y*3.14159265);
      float lag=0.035*tension*weight;
      float x=side*(outer-span*q.x-lag);
      x+=side*0.009*tension*sin(q.x*29.0+q.y*3.0)*sin(q.x*3.14159265);
      float settle=0.006*sin(max(g-0.55,0.0)*22.0)*exp(-max(g-0.55,0.0)*9.0)*smoothstep(0.55,0.65,g);
      x+=side*settle*q.x;
      float y=1.0-2.0*q.y-0.012*tension*weight;
      float z=((0.022*g+0.035*tension)*folds+0.020*tension*weight)*photoSpan;
      return vec3(x,y,z);
    }
    void main(){
      vec2 q=position;
      vec3 p=cloth(q);
      vec3 du=cloth(q+vec2(0.001,0.0))-cloth(q-vec2(0.001,0.0));
      vec3 dv=cloth(q+vec2(0.0,0.001))-cloth(q-vec2(0.0,0.001));
      normal=normalize(cross(du,dv)*side);
      surface=p;
      uv=vec2(side<0.0?q.x*0.5:1.0-q.x*0.5,1.0-q.y);
      gl_Position=vec4(p.x/(1.0-p.z*0.20),p.y,0.0,1.0);
    }`;
  const fragmentSource=`precision mediump float;
    varying mediump vec2 uv; varying mediump vec3 surface; varying mediump vec3 normal;
    uniform sampler2D darkImage;
    uniform vec4 crop; uniform float opening; uniform float lighting; uniform float photoSpan; uniform float portrait;
    float ease(float t){t=clamp(t,0.0,1.0);return t*t*t*(t*(t*6.0-15.0)+10.0);}
    vec2 imageUV(vec2 p){return vec2(crop.x+p.x*crop.z,1.0-(1.0-p.y)*crop.w);}
    void main(){
      if(opening>=1.0)discard;
      vec3 material=texture2D(darkImage,imageUV(uv)).rgb;
      vec3 n=normalize(normal);
      vec3 lightDir=normalize(vec3(0.0,1.55,1.6)-surface);
      float diffuse=max(dot(n,lightDir),0.0);
      vec3 viewDir=normalize(vec3(0.0,0.0,3.0)-surface);
      float shine=pow(max(dot(n,normalize(lightDir+viewDir)),0.0),28.0);
      float movement=smoothstep(0.01,0.20,opening);
      float pool=exp(-pow(surface.x/(mix(0.40,0.25,portrait)+(1.0-surface.y)*0.30),2.0))*exp(-(1.0-surface.y)*0.55);
      vec3 color=material*(vec3(1.0)+mix(vec3(2.2,1.35,0.42),vec3(3.4,2.1,0.7),portrait)*pool*lighting);
      color*=mix(1.0,0.72+diffuse*0.45,movement);
      color+=vec3(0.10,0.055,0.014)*shine*pool*lighting*(0.12+movement*0.88);
      float lip=1.0-smoothstep(0.0,0.006,abs(uv.x-0.5));
      color*=1.0-lip*0.38*movement;
      gl_FragColor=vec4(color,1.0);
    }`;
  function makeWebGL(){
    const gl=canvas.getContext('webgl',{alpha:true,antialias:false,premultipliedAlpha:false});
    if(!gl)throw new Error('Use canvas fallback');
    function shader(type,text){const sh=gl.createShader(type);gl.shaderSource(sh,text);gl.compileShader(sh);if(!gl.getShaderParameter(sh,gl.COMPILE_STATUS))throw new Error(gl.getShaderInfoLog(sh));return sh}
    const program=gl.createProgram();gl.attachShader(program,shader(gl.VERTEX_SHADER,vertexSource));gl.attachShader(program,shader(gl.FRAGMENT_SHADER,fragmentSource));gl.linkProgram(program);if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw new Error(gl.getProgramInfoLog(program));gl.useProgram(program);
    const mesh=[];const nx=160,ny=28;
    for(let y=0;y<ny;y++)for(let x=0;x<nx;x++){const a=x/nx,b=(x+1)/nx,c=y/ny,d=(y+1)/ny;mesh.push(a,c,b,c,a,d,a,d,b,c,b,d)}
    const buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array(mesh),gl.STATIC_DRAW);
    const pos=gl.getAttribLocation(program,'position');gl.enableVertexAttribArray(pos);gl.vertexAttribPointer(pos,2,gl.FLOAT,false,0,0);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);
    gl.activeTexture(gl.TEXTURE0);const tex=gl.createTexture();gl.bindTexture(gl.TEXTURE_2D,tex);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,dark);gl.uniform1i(gl.getUniformLocation(program,'darkImage'),0);
    const op=gl.getUniformLocation(program,'opening'),light=gl.getUniformLocation(program,'lighting'),crop=gl.getUniformLocation(program,'crop'),side=gl.getUniformLocation(program,'side'),span=gl.getUniformLocation(program,'photoSpan'),portrait=gl.getUniformLocation(program,'portrait');
    return ()=>{const scale=Math.max(width/dark.naturalWidth,height/dark.naturalHeight),sw=width/scale,sh=height/scale;gl.viewport(0,0,canvas.width,canvas.height);gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT);gl.uniform4f(crop,(dark.naturalWidth-sw)*.5/dark.naturalWidth,0,sw/dark.naturalWidth,sh/dark.naturalHeight);gl.uniform1f(span,1);gl.uniform1f(portrait,width<=768?1:0);gl.uniform1f(op,opening);gl.uniform1f(light,lighting);for(const value of [-1,1]){gl.uniform1f(side,value);gl.drawArrays(gl.TRIANGLES,0,mesh.length/2)}};
  }
  function makeFallback(){
    const fresh=document.createElement('canvas');fresh.className='jl-curtains';fresh.setAttribute('aria-hidden','true');canvas.replaceWith(fresh);canvas=fresh;
    const ctx=canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    return ()=>{
      const dpr=canvas.width/width;ctx.setTransform(dpr,0,0,dpr,0,0);ctx.clearRect(0,0,width,height);if(opening>=1)return;
      const scale=Math.max(width/dark.naturalWidth,height/dark.naturalHeight),sw=width/scale,sh=height/scale,sx=(dark.naturalWidth-sw)*.5;
      function layer(img,alpha){ctx.globalAlpha=alpha;const rows=Math.ceil(height/5);for(let row=0;row<rows;row++){const yn=(row+.5)/rows,progress=ease(opening+Math.sin(Math.PI*opening)*(.06-.12*yn)),outer=-width*.08*opening,edge=width*.5*(1-progress)-width*.10*opening,span=edge-outer;if(span<=0)continue;const sy=row*sh/rows,dy=row*height/rows;ctx.drawImage(img,sx,sy,sw/2,sh/rows,outer,dy,span+.5,height/rows+.6);ctx.drawImage(img,sx+sw/2,sy,sw/2,sh/rows,width-edge-.5,dy,span+.5,height/rows+.6)}}
      layer(dark,1);ctx.globalAlpha=1;
      if(lighting>0){
        ctx.globalCompositeOperation='source-atop';
        const glow=ctx.createRadialGradient(width*.5,-height*.20,0,width*.5,height*.15,height*1.3);
        glow.addColorStop(0,'rgba(244,185,75,'+(.34*lighting)+')');glow.addColorStop(.45,'rgba(208,142,38,'+(.15*lighting)+')');glow.addColorStop(1,'rgba(150,93,22,0)');
        ctx.fillStyle=glow;ctx.fillRect(0,0,width,height);ctx.globalCompositeOperation='source-over';
      }
    };
  }

  function resize() {
    width = Math.max(1, window.innerWidth);
    height = Math.max(1, window.innerHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    if (renderer) {
      try { renderer(); }
      catch { renderer = null; prepared = false; preparationFailed = true; cancel(); }
    }
  }
  function prepare(retry = false) {
    if (prepared) return Promise.resolve(true);
    if (retry && preparationFailed) preparation = null;
    if (preparation) { if (retry) retryImage?.(); return preparation; }
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve(false);
    preparation = new Promise(resolve => {
      let settled = false;
      let loadTimer;
      const finish = success => {
        if (settled) return;
        settled = true;
        settlePreparation = null;
        clearTimeout(loadTimer);
        retryImage = null;
        dark.onload = dark.onerror = null;
        if (success) {
          try { renderer = makeWebGL(); }
          catch { try { renderer = makeFallback(); } catch { renderer = null; } }
          success = !!renderer;
        }
        prepared = success;
        preparationFailed = !success;
        if (success) resize();
        resolve(prepared);
      };
      settlePreparation = finish;
      loadTimer = setTimeout(() => finish(false), 8000);
      dark.onload = () => finish(true);
      dark.onerror = () => finish(false);
      const source = window.innerWidth <= 768 ? script.dataset.jbCurtainMobile : script.dataset.jbCurtainDesktop;
      retryImage = () => { dark.src = source + (source.includes('?') ? '&' : '?') + 'jb_retry=' + (++loadAttempt); };
      dark.src = source;
      if (dark.complete && dark.naturalWidth) finish(true);
    });
    return preparation;
  }
  function arm() {
    if (!prepared || !renderer) return false;
    opening = 0;
    lighting = 0;
    stage.hidden = false;
    root.classList.add('jb-curtain-active');
    resize();
    return true;
  }
  function cancel() {
    cancelAnimationFrame(frame);
    stage.hidden = true;
    stage.classList.remove('is-opening');
    root.classList.remove('jb-curtain-active');
    const actions = stage.querySelector('[data-jb-curtain-actions]');
    if (actions) actions.hidden = true;
    activePlay = null;
    if (completion) { const resolve = completion; completion = null; resolve(); }
  }
  function play({ canReveal = () => true, retry = () => {} } = {}) {
    if (activePlay) return activePlay;
    if (!arm()) return Promise.resolve();
    activePlay = new Promise(resolve => {
      completion = resolve;
      let elapsed = 0, previous = performance.now();
      let waiting = 0;
      const actions = stage.querySelector('[data-jb-curtain-actions]');
      const darkHold = 180, lightUp = 800, litHold = 120, duration = 2050;
      const total = darkHold + lightUp + litHold + duration;
      function tick(now) {
        const step = Math.min(now - previous, 100);
        previous = now;
        if (!document.hidden) {
          const revealAt = darkHold + lightUp + litHold;
          const revealAllowed = elapsed + step < revealAt || canReveal();
          if (elapsed >= revealAt && !revealAllowed) {
            waiting += step;
            if (actions && waiting >= 8000) actions.hidden = false;
          } else {
            elapsed = revealAllowed ? elapsed + step : Math.min(elapsed + step, revealAt);
            waiting = 0;
            if (actions) actions.hidden = true;
          }
        }
        lighting = ease((elapsed - darkHold) / lightUp);
        opening = clamp((elapsed - darkHold - lightUp - litHold) / duration);
        if (opening > 0) stage.classList.add('is-opening');
        try { renderer(); } catch { cancel(); return; }
        if (elapsed < total) frame = requestAnimationFrame(tick);
        else cancel();
      }
      frame = requestAnimationFrame(tick);
      const again = stage.querySelector('[data-jb-curtain-retry]');
      const skip = stage.querySelector('[data-jb-curtain-continue]');
      if (again) again.onclick = retry;
      if (skip) skip.onclick = cancel;
    });
    return activePlay;
  }
  window.JBCurtainEntrance = { element: stage, prepare, arm, play, cancel, destroy() { cancel(); settlePreparation?.(false); controller.abort(); } };
  canvas.addEventListener('webglcontextlost', event => {
    event.preventDefault();
    try { renderer = makeFallback(); resize(); }
    catch { prepared = false; preparationFailed = true; cancel(); }
  }, { signal: controller.signal });
  window.addEventListener('resize', resize, { signal: controller.signal, passive: true });
  window.addEventListener('pagehide', cancel, { signal: controller.signal });
  document.addEventListener('keydown', event => {
    if (!stage.hidden && event.key === 'Escape') { event.preventDefault(); cancel(); }
  }, { signal: controller.signal });
  if (root.classList.contains('jb-video-loading')) prepare();
  document.dispatchEvent(new Event('jb:curtain-ready'));
})();

